import { useEffect, useRef, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import type { RunDayDivider, Timestamp as FsTimestamp } from '@mct/shared';
import { Button } from '../ui/Button';
import { DateInput } from '../ui/DateInput';
import { Input, Label } from '../ui/Input';

/**
 * Convert a Firestore-shaped Timestamp (the shared interface, not the
 * client SDK class) to a `YYYY-MM-DD` string for the date input. Use
 * UTC accessors so we don't drift across timezones during round-trip.
 */
function tsToDateInput(t: FsTimestamp | null): string {
  if (!t) return '';
  const d = t.toDate();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateInputToTs(v: string): FsTimestamp | null {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  // Anchor at JST midnight so the displayed value stays stable. We
  // build a Firestore-ish Timestamp shape; the actual SDK Timestamp
  // serialises identically when written through updateDoc.
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - 9 * 60 * 60 * 1000;
  return {
    toDate: () => new Date(ms),
    toMillis: () => ms,
    seconds: Math.floor(ms / 1000),
    nanoseconds: 0,
  };
}

/**
 * Floating popover anchored next to a day-divider label chip. Edits
 * label / date / triggers delete, then either commits or cancels.
 */
export function DayDividerEditor({
  divider,
  anchor,
  onSave,
  onDelete,
  onClose,
}: {
  divider: RunDayDivider;
  /** Screen-space rect of the label chip we anchor against. */
  anchor: DOMRect;
  onSave: (next: RunDayDivider) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(divider.label);
  const [dateStr, setDateStr] = useState(tsToDateInput(divider.date));
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on Escape / click-outside.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // defer one tick so the opening click doesn't immediately close us.
    const t = window.setTimeout(
      () => document.addEventListener('mousedown', onClick),
      0,
    );
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        ...divider,
        label: label.trim(),
        date: dateInputToTs(dateStr),
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  // Position: anchor's bottom + 6px, x-centered on the chip.
  const top = anchor.bottom + 6;
  const left = Math.max(8, anchor.left + anchor.width / 2 - 130);

  return (
    <div
      ref={ref}
      className="fixed z-50 w-[260px] rounded-md border border-slate-200 bg-white text-sm shadow-lg"
      style={{ top, left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <span className="text-xs font-semibold text-slate-700">日区切り線</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="rounded p-0.5 text-slate-500 hover:bg-slate-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-2 px-3 py-2">
        <div>
          <Label htmlFor="dd-label">ラベル</Label>
          <Input
            id="dd-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="3/1 / 1日目 / 総点検 etc."
            autoFocus
          />
        </div>
        <div>
          <Label htmlFor="dd-date">日付（任意）</Label>
          <DateInput
            value={dateStr}
            onChange={(next) => setDateStr(next)}
          />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2">
        <Button
          variant="ghost"
          className="!px-2 text-red-600 hover:bg-red-50"
          onClick={async () => {
            setBusy(true);
            try {
              await onDelete();
              onClose();
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          aria-label="削除"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button onClick={save} disabled={busy}>
            保存
          </Button>
        </div>
      </div>
    </div>
  );
}
